import { requireData, requireSuccess } from "../../lib/appError";
import type { Database } from "../../lib/database.types";
import { supabase } from "../../lib/supabaseClient";

type Page = Database["public"]["Tables"]["pages"]["Row"];
type PageInsert = Database["public"]["Tables"]["pages"]["Insert"];
type PageUpdate = Database["public"]["Tables"]["pages"]["Update"];

export const pageRepository = {
  async list(siteVersionId: string): Promise<Page[]> {
    const { data, error } = await supabase
      .from("pages")
      .select("*")
      .eq("site_version_id", siteVersionId)
      .order("navigation_order")
      .order("created_at");
    return requireData(data, error, "Could not load pages.");
  },

  async create(values: PageInsert): Promise<Page> {
    const { data, error } = await supabase
      .from("pages")
      .insert(values)
      .select()
      .single();
    return requireData(data, error, "Could not create the page.");
  },

  async update(pageId: string, values: PageUpdate): Promise<Page> {
    const { data, error } = await supabase
      .from("pages")
      .update(values)
      .eq("id", pageId)
      .select()
      .single();
    return requireData(data, error, "Could not update the page.");
  },

  async remove(pageId: string) {
    const { error } = await supabase.from("pages").delete().eq("id", pageId);
    requireSuccess(error, "Could not delete the page.");
  },

  async duplicate(
    pageId: string,
    title: string,
    slug: string,
  ): Promise<string> {
    const { data, error } = await supabase.rpc("duplicate_page", {
      p_page_id: pageId,
      p_title: title,
      p_slug: slug,
    });
    return requireData(data, error, "Could not duplicate the page.");
  },

  async reorder(siteVersionId: string, pageIds: string[]) {
    const { error } = await supabase.rpc("reorder_pages", {
      p_site_version_id: siteVersionId,
      p_page_ids: pageIds,
    });
    requireSuccess(error, "Could not reorder the pages.");
  },

  async setHome(pageId: string) {
    const { error } = await supabase.rpc("set_home_page", {
      p_page_id: pageId,
    });
    requireSuccess(error, "Could not set the homepage.");
  },
};
